import { For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import { ShieldCheck } from "@fleet/icons";

import { useI18n } from "@/providers/i18n-provider";
import { Skeleton } from "@fleet/ui";
import { QueryErrorState } from "@/components/ui/query-error-state";
import type { MailAuditEntry } from "../api";
import { useMailAudit } from "../hooks";

/**
 * Human labels for the audit vocabulary (`patch.event`). Unknown events (from
 * a newer server) fall back to the raw name rather than disappearing — an
 * audit view must never silently drop rows.
 */
const EVENT_LABELS: Record<string, MessageDescriptor> = {
  connected: msg`Account connected`,
  removed: msg`Account removed`,
  synced: msg`Mailbox synced`,
  exported_eml: msg`Message exported (.eml)`,
  exported_mbox: msg`Folder exported (mbox)`,
  sent: msg`Message sent`,
  rules_updated: msg`Filter rules updated`,
};

function eventName(entry: MailAuditEntry): string {
  return typeof entry.patch.event === "string" ? entry.patch.event : entry.action;
}

/**
 * The mailbox access-audit trail: who used the account, when, and what for.
 * Every row is server-recorded (activity-core) — connects, credential use
 * (sync/send), data egress (.eml/mbox export) and rule edits. Especially
 * relevant for team mailboxes, where several members share one account.
 */
export function MailAuditLog(props: { accountId: string }) {
  const { t, locale } = useI18n();
  const audit = useMailAudit(() => props.accountId);

  const formatTs = (iso: string) => {
    const d = new Date(iso);
    return Number.isNaN(d.getTime())
      ? iso
      : d.toLocaleString(locale(), { dateStyle: "medium", timeStyle: "short" });
  };

  return (
    <div data-testid="mail-audit-log">
      {/* P23 / CC-60 · I3. This is an ACCESS log: saying "No access
          recorded yet" because the API did not respond affirms to the user that
          nobody touched their mailbox, which is exactly the opposite of what
          they came to check. The block is WRAPPED (on error `isLoading` is
          `false` and chaining would leave error + skeleton simultaneously). */}
      <Show when={audit.isError}>
        <QueryErrorState
          testId="mail-audit-error"
          compact
          title={t(msg`Couldn't load the access log`)}
          onRetry={() => void audit.refetch()}
        />
      </Show>
      <Show when={!audit.isError}>
        <Show
          when={!audit.isLoading}
          fallback={
            <div class="flex flex-col gap-2">
              <Skeleton class="h-10 w-full rounded-lg" />
              <Skeleton class="h-10 w-full rounded-lg" />
            </div>
          }
        >
          <Show
            when={audit.isSuccess && (audit.data?.length ?? 0) > 0}
            fallback={
              <p class="text-sm text-muted-foreground" data-testid="mail-audit-empty">
                {t(msg`No access recorded yet.`)}
              </p>
            }
          >
            <ul class="flex flex-col gap-1.5">
              <For each={audit.isSuccess ? audit.data : []}>
                {(entry) => {
                  const name = eventName(entry);
                  const label = EVENT_LABELS[name];
                  return (
                    <li
                      class="flex items-center gap-3 rounded-lg border border-border/40 bg-card/20 px-3 py-2"
                      data-testid="mail-audit-entry"
                      data-event={name}
                    >
                      <ShieldCheck
                        class="h-4 w-4 shrink-0 text-muted-foreground"
                        aria-hidden="true"
                      />
                      <div class="min-w-0 flex-1">
                        <p class="truncate text-sm">{label ? t(label) : name}</p>
                        <p class="truncate text-xs text-muted-foreground/70">
                          {entry.actorName?.trim() || entry.actorUserId} ·{" "}
                          {formatTs(entry.serverTs)}
                        </p>
                      </div>
                    </li>
                  );
                }}
              </For>
            </ul>
          </Show>
        </Show>
      </Show>
    </div>
  );
}
